import { useAnalytics, UserProperty } from '@volumental/analytic';
import { Loader } from '@volumental/common';
import React, { type PropsWithChildren, useEffect } from 'react';
import { useInputStore, useSessionStore, useViewStore } from '../store';
import { AppMode, CurrentView } from '../types/types';

export interface InitialViewProviderProps {
  configuration: {
    scanner: {
      allowSelfScanning: boolean;
      allowAssistedScanning: boolean;
    };
    features: {
      recommendation: {
        enableSurvey: boolean;
      };
    };
  };
}
export function InitialViewProvider({
  configuration: {
    scanner: { allowSelfScanning, allowAssistedScanning },
    features: {
      recommendation: { enableSurvey },
    },
  },
  children,
}: PropsWithChildren<InitialViewProviderProps>): React.JSX.Element {
  const { setUserProperty } = useAnalytics();
  const { setCurrentView } = useViewStore((store) => store);
  const { mode, isInitialized, loadFromQr, token, setInitialized } =
    useSessionStore((store) => store);

  const { scanID, previousPurchaseID } = useInputStore((store) => store);

  useEffect(() => {
    if (!token) {
      return;
    }
    if (isInitialized) {
      return;
    }
    setUserProperty(UserProperty.TenantToken, token);

    let initialView = CurrentView.Intro;

    if (loadFromQr) {
      initialView = CurrentView.Picker;
      if (!enableSurvey && allowSelfScanning && !allowAssistedScanning) {
        initialView = CurrentView.SoloOnboarding;
      }
    } else {
      if (mode === AppMode.Measurement && scanID) {
        initialView = CurrentView.Result;
      }
      if (mode === AppMode.Recommendation && (scanID || previousPurchaseID)) {
        initialView = CurrentView.Result;
      }
      if (mode === AppMode.Sizechart) {
        initialView = CurrentView.SizeChart;
      }
    }

    setCurrentView(initialView);
    setInitialized();
  }, [
    allowAssistedScanning,
    allowSelfScanning,
    enableSurvey,
    isInitialized,
    loadFromQr,
    mode,
    previousPurchaseID,
    scanID,
    setCurrentView,
    setInitialized,
    setUserProperty,
    token,
  ]);

  if (!isInitialized) {
    return <Loader />;
  }

  return children as React.ReactElement;
}
